import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import type {
  BookingSlot,
  TimetableItem,
  WorkCalendarDayOff,
  WorkCalendarEvent,
} from "@/shared/ui/timetable";
import {
  PublicTimetable,
  workCalendarEventsToTimetableItems,
} from "@/shared/ui/timetable";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { Button, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@devhop/ui";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import type { LookupItem } from "@/shared/lib/utils";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type {
  CalendarEventDto,
  LinkedMeetingActionDto,
  LinkedProjectTaskDto,
} from "../../domain/types";
import { useCalendarEvents, useCalendarLinkedItems, useWorkCalendarDayOffs } from "../api/queries";
import { CalendarEventFormDialog } from "../ui/CalendarEventFormDialog";
import type { TimetableItem as TItem } from "@/shared/ui/timetable";

/** §4.3 ວັນສົ່ງ Task / ປະເດັນ: ໃຊ້ເວລາ 08:00–08:30 ເພື່ອແສງໃນ week/day view */
const LINKED_ITEM_START = "08:00";
const LINKED_ITEM_END = "08:30";

function projectTaskToTimetableItem(t: LinkedProjectTaskDto): TItem {
  return {
    id: `task-${t.id}`,
    date: t.dueDate,
    startTime: LINKED_ITEM_START,
    endTime: LINKED_ITEM_END,
    title: t.title,
    type: "project_task",
    projectId: t.projectId,
    ownerId: t.assigneeId ?? undefined,
  };
}

function meetingActionToTimetableItem(a: LinkedMeetingActionDto): TItem {
  return {
    id: `action-${a.id}`,
    date: a.dueDate,
    startTime: LINKED_ITEM_START,
    endTime: LINKED_ITEM_END,
    title: a.description || "ປະເດັນ",
    type: "meeting_action",
    meetingId: a.meetingId,
    ownerId: a.assigneeId ?? undefined,
  };
}

export function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuthState();
  const permissions = usePermissions();
  const canTeam = permissions.has(Permissions.mgmt_calendar.team);
  const canCreate = permissions.has(Permissions.mgmt_calendar.create);
  const canUpdate = permissions.has(Permissions.mgmt_calendar.update);

  const [rangeStart, setRangeStart] = useState(() =>
    format(startOfMonth(new Date()), "yyyy-MM-dd"),
  );
  const [rangeEnd, setRangeEnd] = useState(() =>
    format(endOfMonth(new Date()), "yyyy-MM-dd"),
  );
  const [scopeMode, setScopeMode] = useState<"me" | "other">("me");
  const [scopeUserId, setScopeUserId] = useState<string>("");
  const effectiveUserId =
    scopeMode === "me" ? (user?.id ?? "") : scopeUserId || user?.id || "";

  const [formOpen, setFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEventDto | null>(
    null,
  );
  const [slotForNew, setSlotForNew] = useState<BookingSlot | null>(null);

  const eventsQuery = useCalendarEvents({
    start: rangeStart,
    end: rangeEnd,
    userId: effectiveUserId || undefined,
  });
  const linkedQuery = useCalendarLinkedItems(
    effectiveUserId
      ? { start: rangeStart, end: rangeEnd, userId: effectiveUserId }
      : null,
  );
  const dayOffsQuery = useWorkCalendarDayOffs({
    start: rangeStart,
    end: rangeEnd,
  });

  const events = eventsQuery.data ?? [];
  const linkedData = linkedQuery.data;
  const dayOffs: WorkCalendarDayOff[] = useMemo(
    () =>
      (dayOffsQuery.data ?? []).map((d) => ({
        date: d.date,
        type: d.type,
        description: d.description ?? undefined,
        year: d.year ?? undefined,
      })),
    [dayOffsQuery.data],
  );

  const timetableItems: TimetableItem[] = useMemo(() => {
    const fromEvents = workCalendarEventsToTimetableItems(events as WorkCalendarEvent[]);
    const fromTasks = (linkedData?.projectTasks ?? []).map(projectTaskToTimetableItem);
    const fromActions = (linkedData?.meetingActions ?? []).map(meetingActionToTimetableItem);
    return [...fromEvents, ...fromTasks, ...fromActions];
  }, [events, linkedData]);

  const onRangeChange = useCallback((start: string, end: string) => {
    setRangeStart(start);
    setRangeEnd(end);
  }, []);

  const onSlotSelect = useCallback((slot: BookingSlot) => {
    setSlotForNew(slot);
    setEditingEvent(null);
    setFormOpen(true);
  }, []);

  const onEventClick = useCallback(
    (item: TimetableItem) => {
      if (item.type === "project_task" && item.projectId) {
        navigate({ to: "/app/management/projects/$id", params: { id: item.projectId } });
        return;
      }
      if (item.type === "meeting_action" && item.meetingId) {
        navigate({ to: "/app/management/meetings/$id", params: { id: item.meetingId } });
        return;
      }
      const ev = events.find((e) => e.id === item.id);
      if (ev && canUpdate) {
        setEditingEvent(ev);
        setSlotForNew(null);
        setFormOpen(true);
      }
    },
    [events, navigate, canUpdate],
  );

  const handleAddClick = useCallback(() => {
    setSlotForNew(null);
    setEditingEvent(null);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingEvent(null);
    setSlotForNew(null);
  }, []);

  const handleFormSuccess = useCallback(() => {
    eventsQuery.refetch();
    handleFormClose();
  }, [eventsQuery, handleFormClose]);

  return (
    <>
      <Header>
        <div className="flex flex-wrap items-center gap-2">
          {canTeam && (
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={scopeMode}
                onValueChange={(v) => {
                  setScopeMode(v as "me" | "other");
                  if (v === "me") setScopeUserId("");
                }}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="me">ຂອງຂ້ອຍ</SelectItem>
                  <SelectItem value="other">ເລືອກຜູ້ໃຊ້</SelectItem>
                </SelectContent>
              </Select>
              {scopeMode === "other" && (
                <div className="flex min-w-[220px] flex-col gap-1.5">
                  <Label className="sr-only">ຜູ້ໃຊ້</Label>
                  <InfiniteCombobox<LookupItem>
                    queryKey={["management", "calendar", "user-scope"]}
                    queryFn={(args) =>
                      fetchLookupForInfinite(`${config.apiUrl}/users/lookup`, args)
                    }
                    preloadQueryFn={(id) =>
                      hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
                    }
                    getLabel={(item) => item.name}
                    getValue={(item) => item.id}
                    value={scopeUserId}
                    onValueChange={setScopeUserId}
                    placeholder="ເລືອກຜູ້ໃຊ້"
                    clearable
                  />
                </div>
              )}
            </div>
          )}
          {canCreate && (
            <Button onClick={handleAddClick} size="sm">
              <Plus className="size-4" />
              ເພີ່ມກິດຈະກຳ
            </Button>
          )}
        </div>
      </Header>
      <Main>
        <div className="mb-4">
          <h1 className="font-semibold text-2xl">ປະຕິທິນວຽກ</h1>
          <p className="text-muted-foreground text-sm">
            MANAGEMENT ZONE — ກິດຈະກຳ, ວັນສົ່ງ Task ແລະ ປະເດັນທີ່ລົມກັນ
          </p>
        </div>
        <div className="flex flex-col gap-4">
          <PublicTimetable
            items={timetableItems}
            rooms={[]}
            onSlotSelect={onSlotSelect}
            onEventClick={onEventClick}
            onRangeChange={onRangeChange}
            dayOffs={dayOffs}
            initialView="month"
          />
        </div>
      </Main>

      <CalendarEventFormDialog
        open={formOpen}
        onOpenChange={(open) => !open && handleFormClose()}
        initialSlot={slotForNew}
        initialEvent={editingEvent}
        defaultOwnerId={effectiveUserId || (user?.id ?? "")}
        onSuccess={handleFormSuccess}
      />
    </>
  );
}
